import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, ArrowLeft, Crown, Sparkles, Upload, X, CreditCard, QrCode
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { createPaymentOrder, uploadMembershipScreenshot } from '../services/api';
import { MEMBERSHIP_PLANS } from '../data/services';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Membership = () => {
  const navigate = useNavigate();
  const { user, refreshUser, token } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState('5_years');
  const [loading, setLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cashfree'); // 'cashfree' | 'qr'
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large. Max 10MB.'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    setScreenshot(file);
    const reader = new FileReader();
    reader.onloadend = () => setScreenshotPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleCashfreePayment = async () => {
    if (!token) { toast.error('Please login first'); navigate('/auth'); return; }
    setLoading(true);
    try {
      const res = await createPaymentOrder({ type: 'membership', plan_id: selectedPlan });
      if (res.data.payment_session_id) {
        const cashfree = window.Cashfree({ mode: res.data.cashfree_mode || 'production' });
        cashfree.checkout({
          paymentSessionId: res.data.payment_session_id,
          redirectTarget: '_self'
        });
      } else if (res.data.redirect_url) {
        window.location.href = res.data.redirect_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment gateway error. Try QR code option.');
      setLoading(false);
    }
  };

  const handleQRUpload = async () => {
    if (!screenshot) { toast.error('Please upload payment screenshot'); return; }
    if (!token) { toast.error('Please login first'); navigate('/auth'); return; }
    setLoading(true);
    try {
      const res = await uploadMembershipScreenshot(selectedPlan, screenshot);
      const conf = res.data.verification?.confidence;
      toast.success(`Payment verified${conf ? ` (${conf} confidence)` : ''}! Membership activated!`);
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed. Please try again.');
    } finally { setLoading(false); }
  };

  const plan = MEMBERSHIP_PLANS[selectedPlan];

  if (showPayment) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
            <button onClick={() => { setShowPayment(false); setScreenshot(null); setScreenshotPreview(null); setPaymentMethod('cashfree'); }}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4">
              <ArrowLeft className="w-4 h-4" /> Back to Plans
            </button>

            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Complete Payment</h2>
              <p className="text-slate-500 mt-1">{plan.validity} Membership</p>
            </div>

            {/* Price Breakdown */}
            <div className="bg-gray-50 rounded-xl p-4 mb-5">
              <div className="flex justify-between mb-2 text-sm"><span className="text-gray-600">Plan Price</span><span className="font-medium">Rs.{plan.price}</span></div>
              <div className="flex justify-between mb-2 text-sm"><span className="text-gray-600">GST (18%)</span><span className="font-medium">Rs.{Math.round(plan.price * 0.18)}</span></div>
              <div className="border-t border-gray-200 pt-2 flex justify-between font-bold">
                <span>Total</span>
                <span className="text-amber-600 text-xl">Rs.{plan.total}</span>
              </div>
            </div>

            {/* Payment Method Toggle */}
            <div className="flex bg-gray-100 rounded-xl p-1 mb-5">
              <button onClick={() => setPaymentMethod('cashfree')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-all ${
                  paymentMethod === 'cashfree' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`} data-testid="method-cashfree">
                <CreditCard className="w-4 h-4" /> Pay Online
              </button>
              <button onClick={() => setPaymentMethod('qr')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-all ${
                  paymentMethod === 'qr' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`} data-testid="method-qr">
                <QrCode className="w-4 h-4" /> QR Code
              </button>
            </div>

            {paymentMethod === 'cashfree' ? (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
                  <p className="text-blue-800 text-sm text-center">You'll be redirected to a secure payment page powered by Cashfree.</p>
                </div>
                <button onClick={handleCashfreePayment} disabled={loading}
                  className="w-full py-4 rounded-xl font-semibold text-lg bg-green-600 hover:bg-green-700 text-white shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="cashfree-pay-btn">
                  {loading ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Redirecting...</> : <><CreditCard className="w-5 h-5" /> Pay Rs.{plan.total} Online</>}
                </button>
              </>
            ) : (
              <>
                {/* QR Code */}
                <div className="bg-gray-50 rounded-xl p-4 mb-4 text-center">
                  <p className="text-sm font-medium text-gray-700 mb-3">Scan QR code to pay via UPI</p>
                  <img src="/payment-qr.jpeg" alt="Payment QR Code" className="w-48 h-48 mx-auto rounded-lg object-contain border border-gray-200" data-testid="payment-qr-image" />
                  <p className="text-xs text-gray-500 mt-2">Pay to: SET INDIA BUSINESS PVT LTD</p>
                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    <p className="text-amber-800 font-bold text-lg">Pay Exactly: Rs.{plan.total}</p>
                  </div>
                </div>

                {/* Screenshot Upload */}
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Upload Payment Screenshot *</p>
                  {screenshotPreview ? (
                    <div className="relative">
                      <img src={screenshotPreview} alt="Screenshot" className="w-full h-40 object-contain rounded-xl border border-gray-200 bg-gray-50" />
                      <button onClick={() => { setScreenshot(null); setScreenshotPreview(null); }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <button onClick={() => fileInputRef.current?.click()}
                      className="w-full border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-amber-400 hover:bg-amber-50/50 transition-all cursor-pointer" data-testid="upload-screenshot-btn">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600 font-medium text-sm">Click to upload screenshot</p>
                      <p className="text-xs text-gray-400 mt-1">JPG, PNG (Max 10MB)</p>
                    </button>
                  )}
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
                </div>

                <button onClick={handleQRUpload} disabled={loading || !screenshot}
                  className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
                    screenshot ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`} data-testid="qr-verify-btn">
                  {loading ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Verifying...</> : <><CheckCircle className="w-5 h-5" /> Verify & Activate</>}
                </button>
                <p className="text-center text-xs text-gray-400 mt-2">AI verifies your payment before activation</p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-12 pt-28">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#7C3AED] text-white px-4 py-2 rounded-full mb-6">
            <Crown className="w-5 h-5" /><span className="font-bold">Become a Provider</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Choose Your Membership</h1>
          <p className="text-slate-600 max-w-xl mx-auto">Select a plan to activate your provider profile and start earning</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {Object.values(MEMBERSHIP_PLANS).map((p) => (
            <div key={p.id} onClick={() => setSelectedPlan(p.id)}
              className={`cursor-pointer rounded-2xl p-6 transition-all ${
                selectedPlan === p.id ? 'bg-white ring-4 ring-[#7C3AED]/50 shadow-lg border-2 border-[#7C3AED]/30' : 'bg-white border-2 border-slate-200 hover:border-[#7C3AED]'
              }`} data-testid={`select-plan-${p.id}`}>
              {p.popular && (
                <div className="bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-4 inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Most Popular
                </div>
              )}
              <h3 className="text-xl font-bold mb-2">{p.validity}</h3>
              <div className="mb-2"><span className="text-3xl font-bold text-gray-900">Rs.{p.price}</span></div>
              <div className="text-sm text-gray-500 mb-1">GST (18%): Rs.{Math.round(p.price * 0.18)}</div>
              <div className="text-lg font-semibold text-[#7C3AED] mb-4">Total: Rs.{p.total}</div>
              <ul className="space-y-2">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle className={`w-4 h-4 ${selectedPlan === p.id ? 'text-[#7C3AED]' : 'text-slate-400'}`} /><span>{f}</span>
                  </li>
                ))}
              </ul>
              {selectedPlan === p.id && (
                <div className="mt-4 text-center">
                  <div className="w-6 h-6 bg-[#7C3AED] rounded-full flex items-center justify-center mx-auto"><CheckCircle className="w-4 h-4 text-white" /></div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center">
          <button onClick={() => setShowPayment(true)}
            className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-12 py-4 rounded-full font-semibold text-lg hover:shadow-xl transition-all"
            data-testid="proceed-to-payment-btn">Continue to Payment</button>
          <p className="text-slate-500 mt-4 text-sm">You can always upgrade your plan later</p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Membership;
